import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import toast from 'react-hot-toast';
import { FiUsers, FiChevronRight } from 'react-icons/fi';
import { FaChevronLeft } from 'react-icons/fa';
import { GuardData } from '../../manage_guard/service/guardService';
import { GuardWithAttendance } from './AttendancePage';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { setSalaryDistribution } from '../../../store/slices/attendanceSlice';
import {
  getSalaryDistributionSummary,
  GuardSalaryInfo,
  SalaryDistributionSummary,
  PaymentEntry,
  processSalaryRollout,
} from '../service/paymentService';
import RolloutModal from './RolloutModal';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${theme.colors.light.border.light};
  border-top: 4px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto ${theme.spacing[4]};

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const SearchAndActionsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[3]};
  gap: ${theme.spacing[3]};
  width: 100%;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;
  min-width: 250px;
  
  @media (max-width: ${theme.breakpoints.md}) {
    max-width: 100%;
    width: 100%;
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
  padding: ${theme.spacing[2]} 0;
  border-bottom: 1px solid ${theme.colors.light.border.light};
  background-color: ${theme.colors.light.background.secondary};
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(3, 1fr);
    gap: ${theme.spacing[2]};
  }
`;

const StatCard = styled.div`
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const StatValue = styled.div<{ color?: string }>`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ color }) => color || theme.colors.primary[600]};
  margin-bottom: 2px;
  line-height: 1.2;
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  line-height: 1.2;
`;

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${theme.colors.light.border.light};
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  background-color: ${theme.colors.white};
  position: relative;
`;

const TableBodyWrapper = styled.div<{ showScrollbar?: boolean }>`
  flex: 1;
  min-height: 0;
  overflow: auto;
  position: relative;
  -webkit-overflow-scrolling: touch;
  
  /* Scrollbar overlay only on body - auto-hide */
  scrollbar-width: thin;
  scrollbar-color: ${theme.colors.primary[400]} transparent;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  /* Hide scrollbar buttons (arrows) */
  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, ${theme.colors.primary[400]} 0%, ${theme.colors.primary[500]} 100%);
    border-radius: 4px;
    opacity: ${({ showScrollbar }) => showScrollbar ? 0.8 : 0.3};
    transition: opacity 1s ease 1.5s;
  }
  
  &:hover::-webkit-scrollbar-thumb {
    opacity: 0.9;
    transition: opacity 0.2s ease 0s;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    opacity: 1;
    background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  }
  
  /* Always show scrollbar when scrolling */
  &.scrolling::-webkit-scrollbar-thumb {
    opacity: 0.8;
    transition: opacity 0.2s ease 0s;
  }
  
  &::-webkit-scrollbar-corner {
    background: transparent;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1200px;
  background-color: ${theme.colors.white};
  
  @media (max-width: 1200px) {
    width: 1200px;
    min-width: 1200px;
  }
  
  thead {
    background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
    border-bottom: 2px solid ${theme.colors.primary[200]};
    flex-shrink: 0;
    
    tr:first-child th:first-child {
      border-top-left-radius: ${theme.borderRadius.lg};
    }
    
    tr:first-child th:last-child {
      border-top-right-radius: ${theme.borderRadius.lg};
    }
  }
  
  th {
    text-align: center;
    white-space: normal;
    line-height: 1.4;
    vertical-align: middle;
    font-weight: ${theme.typography.fontWeight.bold};
    padding: ${theme.spacing[5]} ${theme.spacing[6]};
    font-size: ${theme.typography.fontSize.xs};
    color: ${theme.colors.primary[700]};
    text-transform: uppercase;
    letter-spacing: 0.1em;
    position: relative;
    
    &:not(:last-child)::after {
      content: '';
      position: absolute;
      right: 0;
      top: 25%;
      height: 50%;
      width: 1px;
      background: linear-gradient(180deg, transparent, ${theme.colors.primary[300]}, transparent);
    }
  }
  
  td {
    overflow: hidden;
    text-overflow: ellipsis;
    padding: ${theme.spacing[5]} ${theme.spacing[6]};
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.light.text.primary};
    vertical-align: middle;
  }
  
  /* Center align all cells except first column (Guard Info) */
  td:not(:first-child) {
    text-align: center;
  }
  
  /* Keep Guard Info left-aligned */
  td:first-child {
    text-align: left;
  }
  
  /* Column widths */
  th:nth-child(1), td:nth-child(1) { width: 20%; } /* Guard Info */
  th:nth-child(2), td:nth-child(2) { width: 12%; } /* Salary */
  th:nth-child(3), td:nth-child(3) { width: 12%; } /* Advance */
  th:nth-child(4), td:nth-child(4) { width: 12%; } /* Balance */
  th:nth-child(5), td:nth-child(5) { width: 12%; } /* Deduction */
  th:nth-child(6), td:nth-child(6) { width: 20%; } /* Reason */
  th:nth-child(7), td:nth-child(7) { width: 12%; } /* Pending Months */
  
  /* Row hover effect */
  tbody tr {
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: ${theme.colors.primary[50]} !important;
    }
    
    &:nth-child(even) {
      background-color: ${theme.colors.white};
    }
    
    &:nth-child(odd) {
      background-color: ${theme.colors.light.background.secondary};
    }
  }
  
  /* Footer styling - sticky with header matching background */
  tfoot {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.primary[100]} 100%);
    border-top: 2px solid ${theme.colors.primary[200]};
    
    tr:last-child td:first-child {
      border-bottom-left-radius: ${theme.borderRadius.lg};
    }
    
    tr:last-child td:last-child {
      border-bottom-right-radius: ${theme.borderRadius.lg};
    }
    
    td {
      padding: ${theme.spacing[2]} ${theme.spacing[3]};
      text-align: center;
      position: relative;
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing[1]};
  width: 100%;
  flex-wrap: wrap;
  padding: ${theme.spacing[1]} 0;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing[1]};
  }
`;

const PaginationNavButton = styled.button<{ disabled?: boolean; isNext?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background-color: ${({ disabled, isNext }) =>
        disabled
            ? theme.colors.light.background.tertiary
            : isNext
                ? theme.colors.primary[600]
                : theme.colors.light.background.tertiary};
  color: ${({ disabled, isNext }) =>
        disabled
            ? theme.colors.light.text.muted
            : isNext
                ? theme.colors.white
                : theme.colors.light.text.secondary};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: ${({ isNext }) =>
        isNext
            ? theme.colors.primary[700]
            : theme.colors.light.border.medium};
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const PageNumberButton = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${({ active }) => active ? theme.colors.primary[800] : theme.colors.primary[600]};
  background-color: ${theme.colors.white};
  border: none;
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${theme.colors.white};
    color: ${({ active }) => active ? theme.colors.primary[800] : theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(124, 58, 237, 0.15);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Ellipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  color: ${theme.colors.light.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  user-select: none;
`;

interface SalaryDistributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  guards: GuardWithAttendance[];
  companyId: string;
  onUpdate: () => void;
}

const SalaryDistributionModal: React.FC<SalaryDistributionModalProps> = ({
  isOpen,
  onClose,
  guards,
  companyId,
  onUpdate,
}) => {
  const dispatch = useAppDispatch();
  const reduxSummary = useAppSelector((state) => state.attendance.salaryDistribution);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<SalaryDistributionSummary | null>(null);
  const [showRolloutModal, setShowRolloutModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [pdfExported, setPdfExported] = useState(false);
  const [editedPayments, setEditedPayments] = useState<PaymentEntry[] | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tableBodyRef = useRef<HTMLDivElement>(null);
  const pageSize = 15;

  useEffect(() => {
    if (isOpen && guards.length > 0) {
      // Always recalculate when modal opens to get fresh data
      // Don't use cached summary as it might be stale after rollout
      console.log('Calculating salary distribution from guards data');
      setLoading(true);
      const calculateSummary = async () => {
        try {
          // Filter to only deployed guards (have currentDeployment)
          const deployedGuards = guards.filter((g) => {
            return g.currentDeployment && g.currentDeployment.client && g.currentDeployment.postDate;
          });
          
          if (deployedGuards.length === 0) {
            toast.error('No deployed guards found');
            setSummary({
              totalGuards: 0,
              totalAmount: 0,
              totalAdvance: 0,
              totalBalance: 0,
              guards: [],
            });
            setLoading(false);
            return;
          }
          
          // Use only deployed guards data
          const guardData = deployedGuards.map(g => g);
          const summaryData = await getSalaryDistributionSummary(guardData, companyId);
          setSummary(summaryData);
          // Save to Redux for next time
          dispatch(setSalaryDistribution(summaryData));
          console.log('Salary distribution calculated and saved to Redux');
        } catch (error: any) {
          console.error('Error calculating salary distribution:', error);
          toast.error('Failed to calculate salary distribution: ' + (error.message || 'Unknown error'));
        } finally {
          setLoading(false);
        }
      };
      calculateSummary();
    } else if (!isOpen) {
      // Reset when modal closes
      setSummary(null);
    }
  }, [isOpen, guards, companyId, dispatch]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Filter guards based on search term
  const filteredGuards = useMemo(() => {
    if (!summary) return [];
    if (!searchTerm.trim()) {
      return summary.guards;
    }
    const term = searchTerm.toLowerCase();
    return summary.guards.filter((g) => {
      const name = g.guard.name?.toLowerCase() || '';
      const guardId = g.guard.guardId?.toLowerCase() || '';
      const cnic = g.guard.cnic?.toLowerCase() || '';
      return name.includes(term) || guardId.includes(term) || cnic.includes(term);
    });
  }, [searchTerm, summary]);

  // Pagination logic
  const totalPages = Math.ceil(filteredGuards.length / pageSize);
  const paginatedGuards = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredGuards.slice(startIndex, endIndex);
  }, [filteredGuards, currentPage, pageSize]);

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('ellipsis-start');
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis-end');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle scrollbar auto-hide
  useEffect(() => {
    const tableBody = tableBodyRef.current;
    if (!tableBody) return;

    const handleScroll = () => {
      setShowScrollbar(true);
      tableBody.classList.add('scrolling');
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollbar(false);
        tableBody.classList.remove('scrolling');
      }, 2000);
    };

    tableBody.addEventListener('scroll', handleScroll);
    tableBody.addEventListener('mouseenter', () => {
      setShowScrollbar(true);
      tableBody.classList.add('scrolling');
    });
    tableBody.addEventListener('mouseleave', () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      scrollTimeoutRef.current = setTimeout(() => {
        setShowScrollbar(false);
        tableBody.classList.remove('scrolling');
      }, 2000);
    });

    return () => {
      tableBody.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const exportToExcel = () => {
    try {
      const workbook = XLSX.utils.book_new();
      
      // Prepare data for export
      const guardsToExport = filteredGuards.length > 0 ? filteredGuards : (summary?.guards || []);
      
      const exportData = guardsToExport.map((guardInfo) => {
        const editedPayment = editedPayments?.find(p => p.guardId === guardInfo.guardId);
        const displayAmount = editedPayment ? editedPayment.amountToPay : guardInfo.salaryTillToday;
        const displayBalance = editedPayment ? editedPayment.balanceAfter : guardInfo.currentBalance;
        
        return {
          'Guard Name': guardInfo.guard.name || '—',
          'Guard ID': guardInfo.guard.guardId || '—',
          'Salary': `Rs. ${displayAmount.toLocaleString()}`,
          'Advance': `Rs. ${guardInfo.advanceSalary.toLocaleString()}`,
          'Balance': `Rs. ${displayBalance.toLocaleString()}`,
          'Deduction': editedPayment && editedPayment.deductionAmount && editedPayment.deductionAmount > 0
            ? `Rs. ${editedPayment.deductionAmount.toLocaleString()}`
            : 'Rs. 0',
          'Reason': editedPayment && editedPayment.balanceReason ? editedPayment.balanceReason : '—',
          'Pending Months': guardInfo.pendingMonths,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const columnWidths = [
        { wch: 25 }, // Guard Name
        { wch: 15 }, // Guard ID
        { wch: 15 }, // Salary
        { wch: 15 }, // Advance
        { wch: 15 }, // Balance
        { wch: 15 }, // Deduction
        { wch: 30 }, // Reason
        { wch: 15 }, // Pending Months
      ];
      worksheet['!cols'] = columnWidths;
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Salary Distribution');

      const currentDate = new Date().toISOString().split('T')[0];
      const fileName = `Salary_Distribution_${currentDate}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast.success('Excel file exported successfully');
      setPdfExported(true);
    } catch (error: any) {
      console.error('Error exporting Excel:', error);
      toast.error('Failed to export Excel file');
    }
  };

  const exportToPDF = async () => {
    if (!summary) return;

    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPos = 20;

      // Header
      pdf.setFontSize(20);
      pdf.text('Salary Distribution Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      pdf.setFontSize(12);
      const currentDate = new Date();
      pdf.text(`Date: ${currentDate.toLocaleDateString()}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Summary
      pdf.setFontSize(14);
      pdf.text('Summary', 14, yPos);
      yPos += 8;

      pdf.setFontSize(10);
      pdf.text(`Total Guards: ${summary.totalGuards}`, 20, yPos);
      yPos += 6;
      pdf.text(`Total Amount: Rs. ${summary.totalAmount.toLocaleString()}`, 20, yPos);
      yPos += 6;
      pdf.text(`Total Advance: Rs. ${summary.totalAdvance.toLocaleString()}`, 20, yPos);
      yPos += 6;
      pdf.text(`Total Balance: Rs. ${summary.totalBalance.toLocaleString()}`, 20, yPos);
      yPos += 10;

      // Table header
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Guard Name', 14, yPos);
      pdf.text('Guard ID', 50, yPos);
      pdf.text('Salary', 90, yPos);
      pdf.text('Advance', 120, yPos);
      pdf.text('Balance', 150, yPos);
      pdf.text('Deduction', 180, yPos);
      pdf.text('Reason', 210, yPos);
      pdf.text('Months', 250, yPos);
      yPos += 8;

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.line(14, yPos, pageWidth - 14, yPos);
      yPos += 5;

      // Table rows - use edited payments if available, otherwise use filtered guards
      const guardsToExport = filteredGuards.length > 0 ? filteredGuards : summary.guards;
      
      // Export data - check for edited payments for each guard
      for (const guardInfo of guardsToExport) {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = 20;
        }

        // Check if this guard has edited payment data
        const editedPayment = editedPayments?.find(p => p.guardId === guardInfo.guardId);
        
        if (editedPayment) {
          // Export with edited payment data
          pdf.text(editedPayment.guardName || guardInfo.guard.name || '—', 14, yPos);
          pdf.text(editedPayment.guardIdNumber || guardInfo.guard.guardId || '—', 50, yPos);
          pdf.text(`Rs. ${editedPayment.amountToPay.toLocaleString()}`, 90, yPos);
          pdf.text(`Rs. ${editedPayment.advanceSalary.toLocaleString()}`, 120, yPos);
          pdf.text(`Rs. ${editedPayment.balanceAfter.toLocaleString()}`, 150, yPos);
          pdf.text(editedPayment.deductionAmount && editedPayment.deductionAmount > 0 
            ? `Rs. ${editedPayment.deductionAmount.toLocaleString()}` 
            : 'Rs. 0', 180, yPos);
          pdf.text(editedPayment.balanceReason || '—', 210, yPos, { maxWidth: 40 });
          pdf.text(`${guardInfo.pendingMonths}`, 250, yPos);
        } else {
          // Export original data
          pdf.text(guardInfo.guard.name || '—', 14, yPos);
          pdf.text(guardInfo.guard.guardId || '—', 50, yPos);
          pdf.text(`Rs. ${guardInfo.salaryTillToday.toLocaleString()}`, 90, yPos);
          pdf.text(`Rs. ${guardInfo.advanceSalary.toLocaleString()}`, 120, yPos);
          pdf.text(`Rs. ${guardInfo.currentBalance.toLocaleString()}`, 150, yPos);
          pdf.text('Rs. 0', 180, yPos);
          pdf.text('—', 210, yPos);
          pdf.text(`${guardInfo.pendingMonths}`, 250, yPos);
        }
        yPos += 7;
      }

      // Footer
      pdf.setFontSize(8);
      pdf.text(
        `Generated on ${currentDate.toLocaleString()}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );

      pdf.save(`Salary_Distribution_${currentDate.toISOString().split('T')[0]}.pdf`);
      setPdfExported(true);
      toast.success('PDF exported successfully');
    } catch (error: any) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  };

  const handleRolloutComplete = async (payments: PaymentEntry[]) => {
    try {
      setLoading(true);
      await processSalaryRollout(payments, companyId);
      toast.success('Salary rollout completed successfully');
      
      // Clear Redux cache to force reload
      const { clearSalaryDistribution } = await import('../../../store/slices/attendanceSlice');
      dispatch(clearSalaryDistribution());
      
      setShowRolloutModal(false);
      onUpdate(); // This will reload guards data in AttendancePage
      onClose();
    } catch (error: any) {
      console.error('Error processing rollout:', error);
      toast.error(error.message || 'Failed to process salary rollout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen && !showRolloutModal && !showEditModal}
        onClose={onClose}
        title="Salary Distribution"
        size="xl"
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              Close
            </Button>
            <div style={{ display: 'flex', gap: theme.spacing[2] }}>
              <Button
                variant="secondary"
                onClick={() => setShowExportModal(true)}
                disabled={loading || !summary}
              >
                Export
              </Button>
              {pdfExported && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => setShowEditModal(true)}
                    disabled={loading}
                  >
                    Edit
                  </Button>
                  {editedPayments && (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setEditedPayments(null);
                        setShowEditModal(false);
                        toast.success('Changes reset to original values');
                      }}
                      disabled={loading}
                    >
                      Reset
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => setShowRolloutModal(true)}
                    disabled={loading}
                  >
                    Rollout Salary
                  </Button>
                </>
              )}
            </div>
          </div>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          {loading ? (
            <LoadingContainer>
              <Spinner />
              <p style={{ color: theme.colors.light.text.secondary }}>Loading salary distribution...</p>
            </LoadingContainer>
          ) : summary ? (
            <>
              <StatsContainer style={{ flexShrink: 0 }}>
                <StatCard>
                  <StatValue>{summary.totalGuards}</StatValue>
                  <StatLabel>Total Guards</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue color={theme.colors.accent.warning}>
                    Rs. {summary.guards.reduce((sum, g) => sum + (g.previousBalance || 0), 0).toLocaleString()}
                  </StatValue>
                  <StatLabel>Previous Balance</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue color={theme.colors.primary[600]}>
                    Rs. {editedPayments 
                      ? editedPayments.reduce((sum, p) => sum + (p.amountToPay || 0), 0).toLocaleString()
                      : summary.totalAmount.toLocaleString()}
                  </StatValue>
                  <StatLabel>Gross Salary</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue color={theme.colors.accent.info}>
                    Rs. {summary.totalAdvance.toLocaleString()}
                  </StatValue>
                  <StatLabel>Total Advance</StatLabel>
                </StatCard>
                <StatCard>
                  <StatValue color={theme.colors.accent.danger}>
                    Rs. {editedPayments
                      ? editedPayments.reduce((sum, p) => sum + (p.balanceAfter || 0), 0).toLocaleString()
                      : summary.totalBalance.toLocaleString()}
                  </StatValue>
                  <StatLabel>Total Balance</StatLabel>
                </StatCard>
              </StatsContainer>

              <SearchAndActionsContainer style={{ flexShrink: 0 }}>
                <SearchWrapper>
                  <Input
                    placeholder="Search by name, guard ID, or CNIC..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    dark
                  />
                </SearchWrapper>
              </SearchAndActionsContainer>

              <TableWrapper style={{ flex: 1, minHeight: 0 }}>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>Guard<br />Info</th>
                      <th>Previous<br />Balance</th>
                      <th>Gross<br />Salary</th>
                      <th>Advance</th>
                      <th>Balance</th>
                      <th>Deduction</th>
                      <th>Reason</th>
                      <th>Pending<br />Months</th>
                    </tr>
                  </thead>
                </StyledTable>
                <TableBodyWrapper ref={tableBodyRef} showScrollbar={showScrollbar}>
                  <StyledTable>
                    <tbody>
                      {paginatedGuards.map((guardInfo) => {
                        // Check if this guard has edited payment data
                        const editedPayment = editedPayments?.find(p => p.guardId === guardInfo.guardId);
                        const previousBalance = guardInfo.previousBalance || 0;
                        const grossSalary = guardInfo.salaryTillToday - previousBalance; // Gross salary without previous balance
                        const displayAmount = editedPayment ? editedPayment.amountToPay : guardInfo.salaryTillToday;
                        const displayBalance = editedPayment ? editedPayment.balanceAfter : guardInfo.currentBalance;
                        
                        return (
                          <tr key={guardInfo.guardId}>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2] }}>
                                {guardInfo.guard.image ? (
                                  <img
                                    src={guardInfo.guard.image}
                                    alt={guardInfo.guard.name}
                                    style={{
                                      width: 40,
                                      height: 40,
                                      objectFit: 'cover',
                                      borderRadius: '50%',
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      backgroundColor: theme.colors.light.background.tertiary,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: theme.colors.light.text.muted,
                                    }}
                                  >
                                    {guardInfo.guard.name?.charAt(0) || '?'}
                                  </div>
                                )}
                                <div>
                                  <div style={{ fontWeight: theme.typography.fontWeight.medium, color: theme.colors.light.text.primary }}>
                                    {guardInfo.guard.name || '—'}
                                  </div>
                                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.light.text.secondary }}>
                                    {guardInfo.guard.guardId || '—'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td>
                              {previousBalance > 0 ? (
                                <strong style={{ color: theme.colors.accent.warning }}>
                                  Rs. {previousBalance.toLocaleString()}
                                </strong>
                              ) : (
                                'Rs. 0'
                              )}
                            </td>
                            <td>
                              <strong>Rs. {grossSalary.toLocaleString()}</strong>
                            </td>
                            <td>
                              Rs. {guardInfo.advanceSalary.toLocaleString()}
                            </td>
                            <td>
                              <strong style={{ 
                                color: displayBalance > 0 
                                  ? theme.colors.accent.danger 
                                  : theme.colors.accent.success 
                              }}>
                                Rs. {displayBalance.toLocaleString()}
                              </strong>
                            </td>
                            <td>
                              {editedPayment && editedPayment.deductionAmount && editedPayment.deductionAmount > 0
                                ? `Rs. ${editedPayment.deductionAmount.toLocaleString()}`
                                : 'Rs. 0'}
                            </td>
                            <td>
                              {editedPayment && editedPayment.balanceReason
                                ? editedPayment.balanceReason
                                : '—'}
                            </td>
                            <td>
                              {guardInfo.pendingMonths} month(s)
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </StyledTable>
                </TableBodyWrapper>
                <StyledTable>
                  <tfoot>
                    <tr>
                      <td colSpan={8}>
                        <PaginationWrapper>
                          <div style={{ display: 'flex', gap: theme.spacing[2], alignItems: 'center' }}>
                            <PaginationNavButton
                              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={currentPage === 1 || loading}
                            >
                              <FaChevronLeft />
                            </PaginationNavButton>

                            {getPageNumbers().map((page, index) => {
                              if (page === 'ellipsis-start' || page === 'ellipsis-end') {
                                return <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>;
                              }
                              return (
                                <PageNumberButton
                                  key={page}
                                  active={currentPage === page}
                                  onClick={() => setCurrentPage(page as number)}
                                  disabled={loading}
                                >
                                  {page}
                                </PageNumberButton>
                              );
                            })}

                            <PaginationNavButton
                              isNext
                              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                              disabled={currentPage >= totalPages || loading}
                            >
                              <FiChevronRight />
                            </PaginationNavButton>
                          </div>
                        </PaginationWrapper>
                      </td>
                    </tr>
                  </tfoot>
                </StyledTable>
              </TableWrapper>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: theme.spacing[6] }}>
              <p>No salary distribution data available.</p>
            </div>
          )}
        </div>
      </Modal>

      {showEditModal && summary && (
        <RolloutModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          salarySummary={summary}
          companyId={companyId}
          onRollout={async (payments) => {
            // Save temporarily, don't save to DB
            setEditedPayments(payments);
            setShowEditModal(false);
            setPdfExported(true); // Mark as exported so export button shows
            toast.success('Changes saved temporarily. You can export or click Rollout to finalize.');
          }}
          initialPayments={editedPayments || undefined}
          isEditMode={true}
          onExportAfterSave={(payments) => {
            // Save the payments and open export modal
            setEditedPayments(payments);
            setPdfExported(true);
            setShowEditModal(false);
            setShowExportModal(true);
          }}
        />
      )}

      {showRolloutModal && summary && (
        <RolloutModal
          isOpen={showRolloutModal}
          onClose={() => setShowRolloutModal(false)}
          salarySummary={summary}
          companyId={companyId}
          onRollout={handleRolloutComplete}
          initialPayments={editedPayments || undefined}
          isEditMode={false}
        />
      )}

      {/* Export Option Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Salary Distribution"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing[2] }}>
            <Button variant="secondary" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
          </div>
        }
      >
        <div style={{ padding: theme.spacing[4], textAlign: 'center' }}>
          <p style={{ marginBottom: theme.spacing[4], color: theme.colors.light.text.secondary }}>
            Choose export format:
          </p>
          <div style={{ display: 'flex', gap: theme.spacing[3], justifyContent: 'center' }}>
            <Button
              variant="primary"
              onClick={() => {
                exportToPDF();
                setShowExportModal(false);
              }}
              disabled={loading || !summary}
            >
              Export as PDF
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                exportToExcel();
                setShowExportModal(false);
              }}
              disabled={loading || !summary}
            >
              Export as Excel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SalaryDistributionModal;

